<template>
  <Transition name="success-animation">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70 backdrop-blur-sm"
      @click.self="handleClose"
    >
      <div
        class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 transform transition-all"
        @click.stop
      >
        <!-- Success Icon Animation -->
        <div class="flex justify-center mb-6">
          <div class="relative">
            <!-- Animated Circle -->
            <svg
              class="w-24 h-24 text-green-500 animate-scale-in"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke-width="2"
                class="stroke-green-500"
                stroke-dasharray="62.83"
                stroke-dashoffset="62.83"
                :class="{ 'animate-draw-circle': show }"
              />
            </svg>
            
            <!-- Checkmark -->
            <div class="absolute inset-0 flex items-center justify-center">
              <svg
                class="w-12 h-12 text-green-500 transform scale-0 transition-transform duration-300"
                :class="{ 'scale-100': show }"
                style="transition-delay: 0.3s"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                  class="stroke-green-500"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- Success Message -->
        <div class="text-center mb-6">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Nachricht gesendet!
          </h2>
          <p class="text-gray-600 dark:text-gray-300">
            Vielen Dank für Ihre Anfrage. Ich werde mich schnellstmöglich bei Ihnen melden.
          </p>
        </div>

        <!-- Summary -->
        <div
          v-if="summary"
          class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 mb-6 space-y-2 text-sm"
        >
          <div class="flex items-start">
            <svg class="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <div>
              <span class="text-gray-500 dark:text-gray-400">Name:</span>
              <span class="ml-2 text-gray-900 dark:text-white font-medium">{{ summary.name }}</span>
            </div>
          </div>
          <div class="flex items-start">
            <svg class="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <div>
              <span class="text-gray-500 dark:text-gray-400">E-Mail:</span>
              <span class="ml-2 text-gray-900 dark:text-white font-medium">{{ summary.email }}</span>
            </div>
          </div>
        </div>
        <!-- Close Button -->
        <button
          @click="handleClose"
          class="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 dark:from-blue-500 dark:to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-blue-500/50 dark:hover:shadow-blue-600/50 transition-all duration-300"
        >
          Verstanden
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { watch } from 'vue'

interface Props {
  show: boolean
  summary?: {
    name: string
    email: string
  }
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
}>()

const handleClose = () => {
  emit('close')
}

// Auto-close nach 5 Sekunden
watch(() => props.show, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      handleClose()
    }, 5000)
  }
})
</script>

<style scoped>
/* Success Animation */
.success-animation-enter-active,
.success-animation-leave-active {
  transition: opacity 0.3s ease;
}

.success-animation-enter-from,
.success-animation-leave-to {
  opacity: 0;
}

.success-animation-enter-active .bg-white,
.success-animation-leave-active .bg-white {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.success-animation-enter-from .bg-white,
.success-animation-leave-to .bg-white {
  transform: scale(0.9) translateY(-20px);
  opacity: 0;
}

/* Scale In Animation */
@keyframes scale-in {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-scale-in {
  animation: scale-in 0.4s ease-out;
}

/* Draw Circle Animation */
@keyframes draw-circle {
  to {
    stroke-dashoffset: 0;
  }
}

.animate-draw-circle {
  animation: draw-circle 0.6s ease-out forwards;
}
</style>









